import type { Member, Message, PeerState } from './message';

export type Peer = PeerState &
  Member & {
    /** When this peer last sent anything (ms). Drives expiry. */
    seenAt: number;
  };

export type PeerMap = ReadonlyMap<string, Peer>;

/** A peer that has sent nothing for this long loses its focus. */
export const PEER_TTL_MS = 30_000;

/** Quiet peers are re-announced this often so others do not expire them. */
export const HEARTBEAT_MS = 10_000;

export const NO_PEERS: PeerMap = new Map();

function withPeer(peers: PeerMap, peer: Peer): PeerMap {
  return new Map(peers).set(peer.clientId, peer);
}

export function addMember(peers: PeerMap, member: Member, now: number): PeerMap {
  const existing = peers.get(member.clientId);
  return withPeer(peers, { ...existing, ...member, seenAt: now });
}

export function removeMember(peers: PeerMap, clientId: string): PeerMap {
  if (!peers.has(clientId)) return peers;
  const next = new Map(peers);
  next.delete(clientId);
  return next;
}

/**
 * Replaces the member list with an authoritative one, keeping what we know about those still
 * present. A presence answer may be older than what we have heard since asking, so peers in
 * `heard` (joined or spoke while the request was in flight) stay even if the answer omits them.
 */
export function syncMembers(
  peers: PeerMap,
  members: Member[],
  now: number,
  heard: ReadonlySet<string> = new Set(),
): PeerMap {
  const next = new Map<string, Peer>();
  for (const member of members) {
    next.set(member.clientId, {
      ...peers.get(member.clientId),
      ...member,
      seenAt: peers.get(member.clientId)?.seenAt ?? now,
    });
  }
  for (const clientId of heard) {
    const peer = peers.get(clientId);
    if (peer !== undefined && !next.has(clientId)) next.set(clientId, peer);
  }
  return next;
}

function stateOf(message: Message): PeerState {
  switch (message.type) {
    case 'focus':
      return { focus: message.data.field === null ? undefined : message.data };
    case 'state':
      return { focus: message.data.focus?.field == null ? undefined : message.data.focus };
    case 'entity.changed':
    case 'lock.changed':
      return {};
  }
}

/** Folds one message from `from` into the peer map. A `state` message replaces everything we knew. */
export function applyMessage(peers: PeerMap, from: Member, message: Message, now: number): PeerMap {
  if (message.type === 'entity.changed' || message.type === 'lock.changed') return peers;
  return withPeer(peers, { ...from, ...stateOf(message), seenAt: now });
}

/** Clears the focus of peers not heard from within the TTL; they stay as entity members. */
export function expirePeers(peers: PeerMap, now: number, ttlMs = PEER_TTL_MS): PeerMap {
  let next: Map<string, Peer> | null = null;
  for (const peer of peers.values()) {
    const isStale = peer.seenAt < now - ttlMs;
    if (!isStale || !hasState(peer)) continue;
    next ??= new Map(peers);
    next.set(peer.clientId, { clientId: peer.clientId, tab: peer.tab, user: peer.user, seenAt: peer.seenAt });
  }
  return next ?? peers;
}

/** True when the state has anything worth telling a late joiner. */
export function hasState(state: PeerState): boolean {
  return state.focus !== undefined;
}
